import { Analytics } from '@aws-amplify/analytics';
import { AnalyticsEvent, EventAttributes } from '@aws-amplify/analytics/lib-esm/types';
import { Auth } from '@aws-amplify/auth';
import { ClientDevice } from '@aws-amplify/core';
import katalTemplateString from 'src/helpers/katalHelper';
import Config from 'src/analytics/dev-config';
import Attribute from 'src/constants/attribute';
import CookieHelper from 'src/helpers/cookieHelper';
import CookiesConstants from 'src/constants/cookies';
import { kinesisAnalyticsEnabled } from 'src/constants/feature-gates';
import { buildEnvelope, EnvelopeDevice, EnvelopeSession } from 'src/analytics/envelope';
import { getSession, resetSession } from 'src/analytics/session';
import { getClientId, getCognitoId, resetClientIdentity, setCognitoId } from 'src/analytics/clientIdentity';
import { configureTransport, resetTransport, sendEnvelope } from 'src/analytics/transport';

const COGNITO_IDENTITY_POOL_ID = katalTemplateString('{{CognitoIdentityPoolId}}', Config.COGNITO_IDENTITY_POOL_ID_DEV);
const PINPOINT_APP_ID = katalTemplateString('{{PinpointAppId}}', Config.PINPOINT_APP_ID_DEV);

export const SESSION_START = '_session.start';
export const SESSION_STOP = '_session.stop';

const authConfig = {
    Auth: {
        identityPoolId: COGNITO_IDENTITY_POOL_ID,
        region: 'us-west-2',
    }
}

const analyticsConfig = {
    AWSPinpoint: {
        appId: PINPOINT_APP_ID,
        region: 'us-west-2',
        mandatorySignIn: false,
    }
}

const pageViewConfig = {
    enable: true,
    eventName: 'pageView',
    type: 'SPA',
    attributes: {
      referrer: document.referrer,
    },
    provider: 'AWSPinpoint',
    getUrl: () => window.location.href.toString(),
  };

// StrictMode double-invokes componentDidMount. configureTransport carries its own guard, but a
// second autoTrack('pageView') re-enters _pageViewTrackSPA and removes the history patches.
let initialized = false;
let identityResolved = false;
const queued: { event: AnalyticsEvent; now: number }[] = [];

// Amplify's SessionTracker is hardcoded to the Pinpoint provider, so the Kinesis path owns the
// whole session lifecycle.
let onVisibilityChange: (() => void) | undefined;
let onBeforeUnload: (() => void) | undefined;

function initializeAmplify(): void {
    if (initialized) {
        return;
    }
    initialized = true;

    Auth.configure(authConfig);

    if (kinesisAnalyticsEnabled()) {
        configureTransport();
        startSession();
    } else {
        Analytics.configure(analyticsConfig);
        Analytics.autoTrack('pageView', pageViewConfig);
    }

    Analytics.enable();
}

export function resetAnalytics(): void {
    initialized = false;
    identityResolved = false;
    queued.length = 0;
    resetTransport();
    resetSession();
    resetClientIdentity();
    if (onVisibilityChange) {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        onVisibilityChange = undefined;
    }
    if (onBeforeUnload) {
        window.removeEventListener('beforeunload', onBeforeUnload);
        onBeforeUnload = undefined;
    }
}

// Nothing sends until the identity id lands: cognito_id is populated on every record today, and
// screen-visit events fire on mount, before Cognito's round trip completes.
//
// All three statements in the callback are order-dependent. Listeners arm before the drain so a
// throwing send cannot leave the page untracked, and no earlier than here because getSession
// initializes lazily. The start goes out before the drain, carrying openedAt, so it opens the
// session rather than inheriting a start_timestamp from whichever event drained first.
function startSession(): void {
    const openedAt = Date.now();

    Auth.currentCredentials()
        .then((credentials) => setCognitoId(credentials.identityId))
        .catch(() => undefined)
        .then(() => {
            identityResolved = true;
            trackSessionLifecycle();

            send({ name: SESSION_START }, openedAt);

            queued.forEach((entry) => send(entry.event, entry.now));
            queued.length = 0;
        })
        .catch(() => undefined);
}

// Lifecycle events go through dispatch, not recordEvent: neither carries attributes on the live
// stream, so cookie enrichment would populate columns Pinpoint never did.
//
// The unload stop is not reliably delivered on this path: AWSKinesisProvider awaits credentials and
// signing, then fetches without keepalive. See AZTRUST-1254.
function trackSessionLifecycle(): void {
    onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
            stopSession(false);
            return;
        }
        resetSession();
        dispatch({ name: SESSION_START });
    };
    onBeforeUnload = () => stopSession(true);

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('beforeunload', onBeforeUnload);
}

// Both paths clear the session, because _generateSession refreshes the id and start timestamp on
// every start and clears them on every stop. The reset before a start is not redundant with the one
// after a stop: a tab hidden while credentials were still resolving never armed these listeners, so
// the first event they see is the return to visible with no stop before it.
function stopSession(immediate: boolean): void {
    dispatch({ name: SESSION_STOP, immediate });
    resetSession();
}

export function recordEvent(event: AnalyticsEvent): void {
    event.attributes = enrichFromCookies(event.attributes || {}, event.name);
    dispatch(event);
}

// Deliberately skips the cookie enrichment: autoTrack calls Analytics.record directly today, so
// pageView carries exactly url and referrer on the live stream.
export function recordPageView(attributes: EventAttributes): void {
    dispatch({ name: 'pageView', attributes });
}

function dispatch(event: AnalyticsEvent): void {
    if (!kinesisAnalyticsEnabled()) {
        Analytics.record(event);
        return;
    }

    const now = Date.now();
    if (!identityResolved) {
        queued.push({ event, now });
        return;
    }

    send(event, now);
}

function send(event: AnalyticsEvent, now: number): void {
    sendEnvelope(buildEnvelope({
        eventType: event.name,
        attributes: event.attributes || {},
        session: sessionFor(event.name, now),
        client: { clientId: getClientId(), cognitoId: getCognitoId() },
        device: deviceInfo(),
        now,
    }), event.immediate ?? false);
}

function enrichFromCookies(attributes: EventAttributes, eventName: string): EventAttributes {
    const attributeCookieMap = new Map<string, string>();
    attributeCookieMap.set(Attribute.PERSON_ID, CookieHelper.readCookie(CookiesConstants.PERSON_ID));
    attributeCookieMap.set(Attribute.USERNAME, CookieHelper.readCookie(CookiesConstants.USERNAME));
    attributeCookieMap.set(Attribute.REFERRER, CookieHelper.readCookie(CookiesConstants.REFERRER));
    attributeCookieMap.set(Attribute.SESSION_ID, CookieHelper.readCookie(CookiesConstants.SESSION_ID));

    // Only track previous page for screens since the value would change nondeterministically in children components
    if (eventName.includes('Screen')) {
        attributeCookieMap.set(Attribute.PREVIOUS_PAGE, CookieHelper.readCookie(CookiesConstants.PREVIOUS_PAGE));
    }

    return setAttributesFromMap(attributes, attributeCookieMap);
}

function sessionFor(eventName: string, now: number): EnvelopeSession {
    const session = getSession(now);
    return eventName === SESSION_STOP ? { ...session, stopTimestamp: now } : session;
}

function deviceInfo(): EnvelopeDevice {
    const info = ClientDevice.clientInfo();
    return {
        make: info.make ?? '',
        model: info.model ?? '',
        platformName: info.platform ?? '',
    };
}

function setAttributesFromMap(attributes: EventAttributes, attributeCookieMap: Map<string, string>) {
    attributeCookieMap.forEach((value, key) => {
        // we will ignore empty string ('') values in the map when setting the attributes
        if (value) {
            attributes[key] = value;
        }
    } );
    return attributes;
}

export default initializeAmplify;
